import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Home as HomeIcon, Sun, DoorOpen, Trees, Wind, Waves,
  ArrowRight, ArrowLeft, Phone, MapPin, Navigation, FileCheck, ShieldCheck, ChevronDown, Lightbulb, ExternalLink,
  Sparkles,
  Layers,
  RefreshCw,

  Droplets,
  Award, Handshake, Clock,
} from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import LeadForm from "../components/LeadForm";
import NotFound from "./NotFound";
import { brand, services, heroImage } from "../data/site";
import {
  getArea, areaFaqs, distanceLabel, kindLabel, permitAuthority, regionName,
} from "../data/serviceAreas";
import { areaFacts } from "../data/localFacts";

const iconMap = { Home: HomeIcon, Sun, Sparkles, FileCheck, Layers, RefreshCw, Droplets, ShieldCheck, Award, Handshake, Clock };

const buildJsonLd = (area, faqs, url) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "@id": `${url}#service`,
      name: `Roofing & General Contracting in ${area.name}, AZ`,
      serviceType: ["Roof repair", "Roof replacement", "Tile underlayment replacement", "Foam and flat roof coating", "Roof inspection"],
      provider: { "@id": `${SITE_URL}/#business` },
      areaServed: {
        "@type": area.kind === "community" ? "Place" : "City",
        name: `${area.name}, Arizona`,
        address: {
          "@type": "PostalAddress",
          addressLocality: area.name,
          addressRegion: "AZ",
          addressCountry: "US",
        },
        geo: { "@type": "GeoCoordinates", latitude: area.lat, longitude: area.lng },
        containedInPlace: { "@type": "AdministrativeArea", name: "Maricopa County, Arizona" },
      },
      url,
    },
    {
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Service Areas", item: `${SITE_URL}/service-areas` },
        { "@type": "ListItem", position: 3, name: `${area.name}, AZ`, item: url },
      ],
    },
  ],
});

const ServiceArea = () => {
  const { slug } = useParams();
  const area = getArea(slug);
  const [openFaq, setOpenFaq] = useState(0);
  if (!area) return <NotFound />;

  const url = `${SITE_URL}/service-areas/${area.slug}`;
  const faqs = areaFaqs(area);
  const ordered = [
    ...area.focus.map((s) => services.find((x) => x.slug === s)).filter(Boolean),
    ...services.filter((s) => !area.focus.includes(s.slug) && !s.secondary),
  ];
  const nearby = area.nearby.map(getArea).filter(Boolean);
  const facts2 = areaFacts(area.slug);
  const facts = [
    { icon: Navigation, label: "From Phoenix", value: distanceLabel(area) },
    { icon: MapPin, label: "Area type", value: `${kindLabel(area)}, ${regionName(area.region)}` },
    { icon: FileCheck, label: "Permitting authority", value: permitAuthority(area) },
    ...(brand.rocNumber ? [{ icon: ShieldCheck, label: "Arizona license", value: `ROC #${brand.rocNumber}` }] : []),
  ];

  return (
    <div>
      <SEO
        title={`Roof Repair & Replacement in ${area.name}, AZ | Pepla`}
        description={`Roof repair, roof replacement, tile underlayment and free roof inspections in ${area.name}, AZ. Tile, shingle, flat and foam. Free estimates: ${brand.phone}.`}
        jsonLd={buildJsonLd(area, faqs, url)}
      />

      {/* Hero */}
      <section className="relative pt-40 pb-24 section-dark text-white overflow-hidden">
        <div className="absolute inset-0">
          <img fetchPriority="high" decoding="async" src={heroImage} alt={`Roofing and construction in ${area.name}, Arizona by Pepla Roofing`} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/70 to-ink" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-sm text-white/60 mb-6 flex flex-wrap items-center gap-2">
            <Link to="/" className="hover:text-brand transition-colors">Home</Link>
            <span>/</span>
            <Link to="/service-areas" className="hover:text-brand transition-colors">Service Areas</Link>
            <span>/</span>
            <span className="text-white/80">{area.name}</span>
          </nav>
          <span className="kicker-dark">{regionName(area.region)} · Maricopa County</span>
          <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tight mt-3 mb-4">
            Roof Repair &amp; Replacement in {area.name}, AZ
          </h1>
          <div className="brand-line mb-5" />
          <p className="text-white/75 text-lg max-w-2xl">
            Roof repair, roof replacement, tile underlayment and free roof inspections for {area.name} homeowners.
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <a href="#estimate" className="bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors flex items-center gap-2">
              Get a Free Estimate <ArrowRight size={18} />
            </a>
            <a href={`tel:${brand.phoneRaw}`} className="border border-white/30 hover:border-brand text-white hover:text-brand font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors flex items-center gap-2">
              <Phone size={18} /> {brand.phone}
            </a>
          </div>
        </div>
      </section>

      {/* About the area + form */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10 xl:gap-12">
          <div className="lg:col-span-3">
            <span className="kicker">Your Local Contractor</span>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mt-3 mb-4">Serving {area.name}</h2>
            <div className="brand-line mb-6" />
            <p className="text-muted-foreground leading-relaxed text-lg mb-10">{area.intro}</p>

            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {facts.map((f) => (
                <div key={f.label} className="flex items-center gap-4 bg-secondary rounded-xl p-5">
                  <span className="w-11 h-11 rounded-xl bg-brand flex items-center justify-center shrink-0">
                    <f.icon size={20} className="text-ink" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">{f.label}</div>
                    <div className="font-medium text-sm">{f.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {area.zips?.length > 0 && (
              <div>
                <h3 className="font-heading text-lg uppercase tracking-wide mb-3">{area.name} ZIP Codes We Serve</h3>
                <div className="flex flex-wrap gap-2">
                  {area.zips.map((z) => (
                    <span key={z} className="text-xs border border-ink/15 rounded-full px-3 py-1 text-ink/70">{z}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside id="estimate" className="lg:col-span-2 scroll-mt-28">
            <div className="lg:sticky lg:top-28">
              <div className="section-dark text-white rounded-2xl p-6 md:p-8">
                <h3 className="font-heading text-2xl uppercase tracking-wide mb-2">Free {area.name} Estimate</h3>
                <p className="text-white/60 text-sm mb-6">Tell us about your project and we'll be in touch promptly.</p>
                <LeadForm dark defaultCity={area.name} />
              </div>
              <a href={`tel:${brand.phoneRaw}`} className="flex items-center justify-center gap-2 mt-4 border border-ink/15 hover:border-brand rounded-md py-4 font-semibold text-sm transition-colors">
                <Phone size={18} className="text-brand" /> {brand.phone}
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Local know-how */}
      {facts2.length > 0 && (
        <section className="py-24 bg-secondary border-y border-ink/5">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="max-w-2xl mb-12">
              <span className="kicker">Local Know-How</span>
              <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Did You Know? {area.name} Edition</h2>
              <div className="brand-line mb-5" />
              <p className="text-muted-foreground text-lg">
                Little-known facts about roofing in {area.name}, each linked to its source.
              </p>
            </div>
            <div className={`grid grid-cols-1 gap-6 ${facts2.length >= 3 ? "md:grid-cols-2 lg:grid-cols-3" : facts2.length === 2 ? "md:grid-cols-2" : "max-w-3xl"}`}>
              {facts2.map((f, i) => (
                <article key={i} className="card-lift bg-card border border-ink/10 rounded-2xl p-7 flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-10 h-10 rounded-xl bg-ink flex items-center justify-center shrink-0">
                      <Lightbulb size={18} className="text-brand" />
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-widest text-brand-dark">{f.service}</span>
                  </div>
                  <p className="text-ink/80 leading-relaxed flex-1">{f.text}</p>
                  {f.source && (
                    <a
                      href={f.source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-brand transition-colors"
                    >
                      Source: {f.source.label} <ExternalLink size={12} />
                    </a>
                  )}
                </article>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-8">Sources checked September 2026. Rules change, so we confirm current requirements for every project.</p>
          </div>
        </section>
      )}

      {/* Services */}
      <section className="py-24 section-dark text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="kicker-dark">What We Do</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Services in {area.name}</h2>
            <div className="brand-line mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ordered.map((s) => {
              const Icon = iconMap[s.icon];
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group card-lift bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col"
                >
                  <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center mb-4">
                    <Icon size={22} className="text-ink" />
                  </span>
                  <h3 className="font-heading text-2xl uppercase tracking-wide mb-2 group-hover:text-brand transition-colors">
                    {s.name} <span className="sr-only">in {area.name}</span>
                  </h3>
                  <p className="text-white/65 text-sm leading-relaxed flex-1">{s.short}</p>
                  <span className="inline-flex items-center gap-2 text-brand font-semibold text-sm mt-4 uppercase tracking-wide">
                    Learn More <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <span className="kicker">Answers</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">{area.name} FAQs</h2>
            <div className="brand-line mx-auto" />
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={f.q} className="border border-ink/10 rounded-xl overflow-hidden bg-card">
                <h3>
                  <button
                    type="button"
                    aria-expanded={openFaq === i}
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-heading text-lg uppercase tracking-wide">{f.q}</span>
                    <ChevronDown size={20} className={`text-brand shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                </h3>
                {/* Answers stay in the HTML (hidden when collapsed) so search and AI crawlers can read them */}
                <div hidden={openFaq !== i} className="px-6 pb-5 text-muted-foreground text-sm leading-relaxed">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby */}
      <section className="py-20 section-dark text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-heading text-3xl uppercase tracking-tight mb-3">Also Serving Near {area.name}</h2>
          <div className="brand-line mx-auto mb-8" />
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {nearby.map((n) => (
              <Link
                key={n.slug}
                to={`/service-areas/${n.slug}`}
                className="text-sm border border-white/15 rounded-full px-4 py-2 text-white/80 hover:border-brand hover:text-brand transition-colors"
              >
                {n.name}
              </Link>
            ))}
          </div>
          <Link to="/service-areas" className="inline-flex items-center gap-2 text-brand font-semibold uppercase tracking-wide text-sm link-underline">
            <ArrowLeft size={16} /> All Service Areas
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceArea;
